"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCatalogActivityProgress = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const graphql_1 = require("../../../__generated__/graphql");
const ramda_1 = require("ramda");
const catalog_progress_utils_1 = require("./catalog-progress.utils");
const catalog_models_1 = require("./catalog.models");
const get_catalog_activity_progress_query_1 = require("./get-catalog-activity-progress.query");
const attributes_1 = require("../attributes");
const constants_1 = require("../ticket/hooks/constants");
const POLL_INTERVAL = 5000;
const EXTRACT_TICKET_TITLE = constants_1.ticketTypeToContentMap[constants_1.TicketType.Extract].title;
const CATALOG_WORKFLOW_TICKET_TITLES = [
    constants_1.TicketType.CatalogImport,
    constants_1.TicketType.Categorize,
    constants_1.TicketType.Extract,
    constants_1.TicketType.Cluster,
    constants_1.TicketType.CatalogExport,
    constants_1.TicketType.Migrate,
].map((ticketType) => constants_1.ticketTypeToContentMap[ticketType].title);
const useCatalogActivityProgress = ({ selectedCatalogId }) => {
    const { data, error } = (0, react_2.useQuery)(get_catalog_activity_progress_query_1.GET_CATALOG_ACTIVITY_PROGRESS, {
        variables: {
            selectedCatalogId: selectedCatalogId || '',
            extractTicketTitle: EXTRACT_TICKET_TITLE,
            catalogWorkflowTicketTitles: CATALOG_WORKFLOW_TICKET_TITLES,
        },
        skip: !selectedCatalogId,
        pollInterval: POLL_INTERVAL,
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const previousTasksRef = (0, react_1.useRef)([]);
    // Process GraphQL data into ReEnrichTask[] and preserve the previous array reference
    // when the polled payload is semantically unchanged. This keeps the provider value
    // stable and avoids re-rendering all consumers every 5 seconds.
    const tasks = (0, react_1.useMemo)(() => {
        var _a, _b;
        let nextTasks;
        if (!((_b = (_a = data === null || data === void 0 ? void 0 : data.catalog) === null || _a === void 0 ? void 0 : _a.extractTickets) === null || _b === void 0 ? void 0 : _b.edges)) {
            nextTasks = [];
        }
        else {
            nextTasks = data.catalog.extractTickets.edges.flatMap(({ node }) => {
                var _a, _b, _c;
                const { ticket } = node;
                const linkedNodes = ((_a = ticket.linkedTo) === null || _a === void 0 ? void 0 : _a.edges) || [];
                const productNodeTasks = [];
                for (const linkedNode of linkedNodes) {
                    const { reference } = linkedNode.node;
                    if (reference.__typename === 'ProductTreeNode') {
                        const ancestorNodeIds = ((_c = (_b = reference.ancestors) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.map(({ node: ancestorNode }) => ancestorNode.ancestor.id).filter((ancestorId) => ancestorId !== reference.id)) || [];
                        const targetType = reference.title === attributes_1.ONTOLOGY_NODE_TITLE ? 'root' : 'node';
                        productNodeTasks.push({
                            ticketId: ticket.id,
                            status: ticket.status,
                            targetType,
                            targetNodeId: reference.id,
                            targetNodeTitle: reference.title,
                            ancestorNodeIds,
                        });
                    }
                }
                if (productNodeTasks.length > 0) {
                    return productNodeTasks;
                }
                return [{
                        ticketId: ticket.id,
                        status: ticket.status,
                        targetType: 'catalog',
                        ancestorNodeIds: [],
                    }];
            });
        }
        if ((0, ramda_1.equals)(previousTasksRef.current, nextTasks)) {
            return previousTasksRef.current;
        }
        previousTasksRef.current = nextTasks;
        return nextTasks;
    }, [data]);
    const catalogActivity = (0, react_1.useMemo)(() => {
        if (!(data === null || data === void 0 ? void 0 : data.catalog)) {
            return {
                isUploadInProgress: false,
                catalogProgressState: catalog_models_1.CatalogProgressState.NotStarted,
                catalogProgress: 0,
            };
        }
        const { openWorkflowTickets, pendingWorkflowTickets, closedWorkflowTickets } = data.catalog;
        const openTicketTitles = openWorkflowTickets.edges.map(({ node }) => node.ticket.title);
        const pendingTicketTitles = pendingWorkflowTickets.edges
            .filter(({ node }) => node.ticket.title !== constants_1.ticketTypeToContentMap[constants_1.TicketType.Migrate].title
            || node.ticket.status !== graphql_1.TicketStatus.Canceled)
            .map(({ node }) => node.ticket.title);
        const closedMigrateTicketTitles = closedWorkflowTickets.edges
            .map(({ node }) => node.ticket.title)
            .filter((title) => title === constants_1.ticketTypeToContentMap[constants_1.TicketType.Migrate].title);
        const workflowProgress = {
            openCount: openWorkflowTickets.totalCount,
            pendingCount: pendingWorkflowTickets.totalCount,
            closedCount: closedWorkflowTickets.totalCount,
            pendingTicketTitles,
        };
        return (0, catalog_progress_utils_1.getCatalogActivityProgress)(workflowProgress, [
            ...openTicketTitles,
            ...pendingTicketTitles,
            ...closedMigrateTicketTitles,
        ]);
    }, [data]);
    return Object.assign(Object.assign({ tasks }, catalogActivity), { error });
};
exports.useCatalogActivityProgress = useCatalogActivityProgress;
